import { SearchOutlined } from "@ant-design/icons";
import { Button, Form, Input, Select, Typography } from "antd";
import { descriptionRules, nameRules } from "common/FormValidationRules";
import FlexContainerLayout from "components/atoms/FlexContainerLayout";
import TagSearchInput from "components/atoms/TagSearchInput";
import { useEffect, useState } from "react";
import CypressTestIds from "../../../cypress/CypressTestIds";

const { Option } = Select;
const DEFAULT_BUSINESS_AREAS = [{ key: "all", value: "All Business Areas" }];
const DEFAULT_SCHEMA_LIST = [{ key: "all", value: "All Schemas" }];
const DEFAULT_DATA_SET_LIST = [{ key: "all", value: "All Datasets" }];

interface IDataObjectSearchDuplicateFormProps {
    defaultSearchValues?: string[];
    businessAreas: IBusinessArea[];
    onBusinessAreaChange: (id: string) => void;
    loadingSchemaList?: boolean;
    schemaList: {
        Items: ISchema[];
    };
    onSchemaChange: (bId: string, sId: string) => void;
    loadingDataSetList?: boolean;
    dataSetList: {
        Items: IDataSet[];
    };
    loadingDataTags?: boolean;
    dataTags: IDataTag[];
    onSearch: (i: IDataObjectDuplicateSearchInput) => void;
    onCancel?: () => void;
}

const DataObjectSearchDuplicateForm = ({
    defaultSearchValues,
    businessAreas,
    onBusinessAreaChange,
    loadingSchemaList,
    schemaList,
    onSchemaChange,
    loadingDataSetList,
    dataSetList,
    loadingDataTags,
    dataTags,
    onSearch,
    onCancel,
}: IDataObjectSearchDuplicateFormProps) => {
    const [form] = Form.useForm();
    const [businessAreaOptions, setBusinessAreaOptions] = useState(DEFAULT_BUSINESS_AREAS);
    const [schemaOptions, setSchemaOptions] = useState(DEFAULT_SCHEMA_LIST);
    const [dataSetOptions, setDataSetOptions] = useState(DEFAULT_DATA_SET_LIST);

    useEffect(() => {
        setBusinessAreaOptions([...DEFAULT_BUSINESS_AREAS, ...businessAreas.map((item) => ({ key: item.businessAreaId, value: item.name }))]);
    }, [businessAreas]);

    useEffect(() => {
        setSchemaOptions([...DEFAULT_SCHEMA_LIST, ...schemaList.Items.map((item) => ({ key: item.schemaId, value: item.name }))]);
    }, [schemaList]);

    useEffect(() => {
        setDataSetOptions([...DEFAULT_DATA_SET_LIST, ...dataSetList.Items.map((item) => ({ key: item.dataSetId, value: item.dataSetName }))]);
    }, [dataSetList]);

    const onFormValuesChanged = (changedValues: Partial<IDataObjectDuplicateSearchInput>, allValues: IDataObjectDuplicateSearchInput) => {
        if (changedValues.businessArea) {
            allValues["schema"] = DEFAULT_SCHEMA_LIST[0].key;
            allValues["dataSet"] = DEFAULT_DATA_SET_LIST[0].key;
            form.setFieldsValue(allValues);
            setSchemaOptions(DEFAULT_SCHEMA_LIST);
            setDataSetOptions(DEFAULT_DATA_SET_LIST);
            onBusinessAreaChange(changedValues.businessArea);
        } else if (changedValues.schema) {
            allValues["dataSet"] = DEFAULT_DATA_SET_LIST[0].key;
            form.setFieldsValue(allValues);
            setDataSetOptions(DEFAULT_DATA_SET_LIST);
            onSchemaChange(allValues.businessArea, changedValues.schema);
        }
    };

    return (
        <FlexContainerLayout data-testid={CypressTestIds.DUPLICATE_SEARCH_CONFIG_CONTAINER} header={<Typography.Title level={4}>New Search Configuration</Typography.Title>}>
            <Form<IDataObjectDuplicateSearchInput>
                form={form}
                initialValues={defaultSearchValues}
                name="basic"
                layout="vertical"
                onFinish={onSearch}
                onValuesChange={onFormValuesChanged}
            >
                <FlexContainerLayout>
                    <div className="flex w-full flex-row gap-4">
                        <div className="flex-1">
                            <Form.Item initialValue={DEFAULT_BUSINESS_AREAS[0].key} name="businessArea" className="mb-2">
                                <Select placeholder="Please select business area" data-testid={CypressTestIds.DUPLICATE_SEARCH_CONFIG_BUSINESS_AREA_SELECT} virtual={false}>
                                    {businessAreaOptions.map((option) => (
                                        <Option key={option.key} value={option.key} data-testid={option.value.split(" ").join("-").toLowerCase()}>
                                            {option.value}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </div>
                        <div className="flex-1">
                            <Form.Item initialValue={DEFAULT_SCHEMA_LIST[0].key} name="schema" className="mb-2">
                                <Select
                                    placeholder="Please select schema"
                                    disabled={loadingSchemaList}
                                    loading={loadingSchemaList}
                                    data-testid={CypressTestIds.DUPLICATE_SEARCH_CONFIG_SCHEMA_SELECT}
                                    virtual={false}
                                >
                                    {schemaOptions.map((option) => (
                                        <Option key={option.key} value={option.key} data-testid={option.value.split(" ").join("-").toLowerCase()}>
                                            {option.value}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </div>
                        <div className="flex-1">
                            <Form.Item initialValue={DEFAULT_DATA_SET_LIST[0].key} name="dataSet" className="mb-2">
                                <Select
                                    placeholder="Please select dataset"
                                    disabled={loadingDataSetList || loadingSchemaList}
                                    loading={loadingDataSetList || loadingSchemaList}
                                    data-testid={CypressTestIds.DUPLICATE_SEARCH_CONFIG__DATASET_SELECT}
                                    virtual={false}
                                >
                                    {dataSetOptions.map((option) => (
                                        <Option key={option.key} value={option.key} data-testid={option.value.split(" ").join("-").toLowerCase()}>
                                            {option.value}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </div>
                    </div>
                    <div className="flex w-full flex-row gap-4">
                        <div className="flex-1">
                            <Form.Item name={["name"]} rules={nameRules} validateFirst={true} className="mb-2">
                                <Input placeholder="Search configuration name" data-testid={CypressTestIds.DUPLICATE_SEARCH_CONFIG_NAME_INPUT} />
                            </Form.Item>
                        </div>
                        <div className="flex-1">
                            <Form.Item name={["description"]} rules={descriptionRules} className="mb-2">
                                <Input placeholder="Search configuration description" data-testid={CypressTestIds.DUPLICATE_SEARCH_CONFIG_DESCRIPTION_INPUT} />
                            </Form.Item>
                        </div>
                        <div className="flex-1">
                            <Form.Item name="dataTags" className="mb-2">
                                <TagSearchInput
                                    loadingDataTags={loadingDataTags}
                                    dataTags={dataTags}
                                    data-testid={CypressTestIds.DUPLICATE_SEARCH_CONFIG_TAG_SEARCH_SELECT}
                                ></TagSearchInput>
                            </Form.Item>
                        </div>
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button onClick={onCancel} data-testid={CypressTestIds.DUPLICATE_SEARCH_CONFIG_CLOSE_BUTTON}>
                            Close
                        </Button>
                        <div className="flex-none">
                            <Button
                                type="primary"
                                icon={<SearchOutlined />}
                                iconPlacement="end"
                                onClick={() => form.submit()}
                                data-testid={CypressTestIds.DUPLICATE_SEARCH_CONFIG_SEARCH_BUTTON}
                                loading={loadingSchemaList || loadingDataSetList}
                                disabled={loadingSchemaList || loadingDataSetList}
                            >
                                Run Search Now
                            </Button>
                        </div>
                    </div>
                </FlexContainerLayout>
            </Form>
        </FlexContainerLayout>
    );
};

export default DataObjectSearchDuplicateForm;
